import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { GridSectionContent, PageSectionProps, GridItem } from '@/types/cms';
import { cn } from '@/lib/utils';
import * as Icons from 'lucide-react';
import { Sparkles } from 'lucide-react';

interface GridSectionProps extends PageSectionProps {
  content: GridSectionContent;
}

const GridSection: React.FC<GridSectionProps> = ({ 
  section, 
  content, 
  className,
  isPreview = false
}) => {
  const {
    columns = 2,
    items = [],
    card_style = 'default'
  } = content as GridSectionContent;

  // Dynamic icon component
  const IconComponent = ({ iconName }: { iconName?: string }) => {
    if (!iconName) return null;
    
    const Icon = (Icons as any)[iconName];
    if (!Icon) return null;
    
    return <Icon className="h-8 w-8 text-primary" />;
  };

  // Helper function to get responsive class prefix
  const getResponsivePrefix = () => isPreview ? '@' : '';
  
  // Grid columns responsive classes
  const gridClasses = cn(
    "grid gap-6",
    {
      "grid-cols-1": columns === 1,
      [`grid-cols-1 ${getResponsivePrefix()}md:grid-cols-2`]: columns === 2,
      [`grid-cols-1 ${getResponsivePrefix()}md:grid-cols-2 ${getResponsivePrefix()}lg:grid-cols-3`]: columns === 3,
      [`grid-cols-1 ${getResponsivePrefix()}md:grid-cols-2 ${getResponsivePrefix()}lg:grid-cols-4`]: columns === 4,
      [`grid-cols-1 ${getResponsivePrefix()}md:grid-cols-2 ${getResponsivePrefix()}lg:grid-cols-5`]: columns === 5,
    }
  );

  // Enhanced card style classes with ServiceDetails patterns
  const cardClasses = cn(
    "h-full transition-all duration-300",
    {
      "border-0 shadow-none bg-transparent": card_style === 'minimal',
      "bg-gradient-to-br from-white to-gray-50/30 border shadow-sm hover:shadow-md": card_style === 'bordered',
      "bg-gradient-to-br from-white to-gray-50/30 border-0 shadow-lg hover:shadow-xl": card_style === 'elevated',
      "bg-gradient-to-br from-white to-gray-50/30 border shadow-sm hover:shadow-md hover:border-primary/50": card_style === 'default',
    }
  );

  const GridItemComponent: React.FC<{ item: GridItem; index: number }> = ({ item, index }) => {
    const {
      title,
      description,
      icon,
      image_url,
      link
    } = item;

    const content = (
      <Card className={cardClasses}>
        <CardHeader className={cn(
          "text-center pb-3",
          card_style === 'minimal' ? "pb-4" : ""
        )}>
          {/* Enhanced Icon or Image */}
          {icon && (
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <IconComponent iconName={icon} />
              </div>
            </div>
          )}
          
          {image_url && (
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full overflow-hidden shadow-md">
                <img 
                  src={image_url} 
                  alt={title || `Grid item ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}

          {/* Enhanced Title */}
          {title && (
            <CardTitle className="text-lg font-semibold text-gray-900 leading-tight">
              {title}
            </CardTitle>
          )}
        </CardHeader>

        {/* Enhanced Description */}
        {description && (
          <CardContent className={cn(
            "text-center pt-0",
            card_style === 'minimal' ? "pt-0" : ""
          )}>
            <p className="text-base text-gray-700 leading-relaxed">
              {description}
            </p>
          </CardContent>
        )}
      </Card>
    );

    // Wrap with link if provided
    if (link) {
      return (
        <a 
          href={link}
          className="block group hover:scale-105 transition-transform duration-300"
        >
          {content}
        </a>
      );
    }

    return content;
  };

  return (
    <section className={cn("py-16 px-4", className)}>
      <div className="max-w-6xl mx-auto">
        {/* Enhanced Section Header */}
        {section.title && (
          <div className="text-center mb-12">
            <h2 className={`text-3xl ${getResponsivePrefix()}md:text-4xl font-bold mb-4 text-gray-900 leading-tight`}>
              {section.title}
            </h2>
          </div>
        )}

        {/* Grid Content with improved spacing */}
        <div className={cn(gridClasses, "gap-8")}>
          {items.map((item, index) => (
            <GridItemComponent 
              key={index} 
              item={item} 
              index={index}
            />
          ))}
        </div>

        {/* Enhanced empty state with icon */}
        {items.length === 0 && (
          <Card className="bg-gradient-to-br from-white to-gray-50/30 border shadow-sm">
            <CardContent className="text-center py-16 space-y-4">
              <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <div className="space-y-2">
                <p className="text-lg font-medium text-gray-900">No content configured yet</p>
                <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
                  This section will display beautiful grid items once configured through the admin panel.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  );
};

export default GridSection;